// Updated version of CitizenExam.js
// Simplified with citizen-aware filtering by age group / gender / visit context
// while preserving existing load/save/event behavior and tenant-safe navigation.

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Typography,
  TextField,
  Box,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import EncounterHeader from '../../components/EncounterHeader';
import useVisitReadOnly from '../../useVisitReadOnly';
import http from '../../services/http';
import { getSectionModulePlan, moduleLabels } from '../../clinical/visitSectionModules';

// Utility to read JSON safely
const jget = (k) => {
  try {
    return JSON.parse(localStorage.getItem(k) || 'null');
  } catch {
    return null;
  }
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function toNum(v) {
  if (v === '' || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function getAgeNumber(val) {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  const s = String(val ?? '').trim();
  if (!s) return null;
  const m = s.match(/(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

function normalizeGender(val) {
  const s = String(val || '').trim().toLowerCase();
  if (!s) return 'unknown';
  if (['male', 'm', 'man', 'boy'].includes(s)) return 'male';
  if (['female', 'f', 'woman', 'girl'].includes(s)) return 'female';
  if (s.includes('trans')) return 'transgender';
  if (s.includes('non-binary') || s.includes('non binary') || s.includes('nonbinary')) return 'other';
  return s;
}

function getAgeGroup(age) {
  if (age == null) return 'unknown';
  if (age <= 4) return 'under5';
  if (age <= 9) return 'child';
  if (age <= 19) return 'adolescent';
  if (age <= 59) return 'adult';
  return 'geriatric';
}

function ageGroupLabel(group) {
  if (group === 'under5') return 'Under-5';
  if (group === 'child') return '5–9 years';
  if (group === 'adolescent') return '10–19 years';
  if (group === 'adult') return '20–59 years';
  if (group === 'geriatric') return '60+ years';
  return 'Not derived';
}

function formatAgeAtVisit(visit = {}, fallbackYears = null) {
  const days = Number(visit?.ageInDaysAtVisit);
  if (Number.isFinite(days) && days >= 0) {
    if (days < 60) {
      const wholeDays = Math.floor(days);
      return `${wholeDays} day${wholeDays === 1 ? '' : 's'}`;
    }
    if (days < 365.2425) {
      const months = Math.floor(days / 30.4375);
      const remainingDays = Math.max(0, Math.floor(days - (months * 30.4375)));
      return `${months} month${months === 1 ? '' : 's'}${remainingDays ? ` ${remainingDays} day${remainingDays === 1 ? '' : 's'}` : ''}`;
    }
    const years = Math.floor(days / 365.2425);
    const remainingDays = days - Math.floor(years * 365.2425);
    const months = Math.max(0, Math.floor(remainingDays / 30.4375));
    return `${years} year${years === 1 ? '' : 's'}${months ? ` ${months} month${months === 1 ? '' : 's'}` : ''}`;
  }
  if (fallbackYears == null || !Number.isFinite(Number(fallbackYears))) return '';
  return `${Math.floor(Number(fallbackYears))} years`;
}

// tenant-safe navigation helpers
function getTenantBase() {
  try {
    const m = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return m ? m[0] : '';
  } catch {
    return '';
  }
}
function tenantUrl(path) {
  const base = getTenantBase();
  const p = String(path || '');
  const clean = p.startsWith('/') ? p : `/${p}`;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

function Section({ title, children }) {
  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

const SYSTEMIC_EXAM_FIELDS = [
  { name: 'respiratory', label: 'Respiratory', planKey: 'respiratory', core: true },
  { name: 'cardiovascular', label: 'Cardiovascular', planKey: 'cardiovascular', core: true },
  { name: 'gastrointestinal', label: 'Gastrointestinal / abdomen', planKey: 'gastrointestinal', core: true },
  { name: 'nervous', label: 'Neurological', planKey: 'neurological', core: true },
  { name: 'musculoskeletal', label: 'Musculoskeletal', planKey: 'musculoskeletal' },
  { name: 'urinary', label: 'Urinary / renal', planKey: 'urinary' },
  { name: 'skin', label: 'Skin / allergy', planKey: 'skin' },
  { name: 'ent', label: 'ENT / hearing', planKey: 'ent' },
  { name: 'eyes', label: 'Eye', planKey: 'eyes' },
  { name: 'reproductive', label: 'Reproductive / pelvic', planKey: 'reproductive' },
];

export default function CitizenExam({
  embedded = false,
  visitId: visitIdProp = '',
  citizenId: citizenIdProp = '',
  visit: workspaceVisit = null,
  onSectionDirty,
  onSectionSaved,
  onCanSave,
  onSavingChange,
  registerSave,
}) {
  const theme = useTheme();
  const query = useQuery();
  const params = useParams();
  const navigate = useNavigate();

  const user = jget('loggedInUser') || {};
  const role = String(user.role || user.userType || '').toLowerCase();
  const isIntern = role.includes('intern');
  const isCitizen = role === 'citizen';

  const [visitId, setVisitId] = useState('');
  const [citizenId, setCitizenId] = useState('');
  const [form, setForm] = useState({
    vitals: {},
    generalExam: {},
    systemicExam: {},
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');

  const fieldProps =
    theme.palette.mode === 'dark'
      ? { InputLabelProps: { style: { color: '#fff' } }, inputProps: { style: { color: '#fff' } } }
      : { InputLabelProps: { style: { color: 'inherit' } }, inputProps: { style: { color: 'inherit' } } };

  const activeCitizen = useMemo(() => {
    const stored = jget('activeCitizen') || {};
    if (!workspaceVisit) return stored;

    return {
      ...stored,
      citizenId: workspaceVisit.citizenId || stored.citizenId || '',
      name: workspaceVisit.citizenName || stored.name || '',
      gender: workspaceVisit.citizenGender || stored.gender || '',
      age:
        workspaceVisit.ageYearsAtVisit ??
        workspaceVisit.citizenAge ??
        stored.age ??
        '',
    };
  }, [workspaceVisit]);

  const visitContext = useMemo(() => {
    if (workspaceVisit) return workspaceVisit;
    const scoped = visitId ? jget(`activeVisitContext:${visitId}`) : null;
    return scoped || jget('activeVisitContext') || {};
  }, [visitId, workspaceVisit]);

  const citizenAge = useMemo(
    () =>
      getAgeNumber(
        workspaceVisit?.ageYearsAtVisit ??
          workspaceVisit?.citizenAge ??
          activeCitizen?.age
      ),
    [workspaceVisit, activeCitizen]
  );
  const citizenGender = useMemo(
    () => normalizeGender(workspaceVisit?.citizenGender || activeCitizen?.gender),
    [workspaceVisit, activeCitizen]
  );
  const citizenAgeGroup = useMemo(() => getAgeGroup(citizenAge), [citizenAge]);

  const visitCategory = useMemo(
    () => String(visitContext?.visitCategory || query.get('visitCategory') || '').trim(),
    [visitContext, query]
  );
  const clinicalPathway = useMemo(
    () => String(visitContext?.clinicalPathway || query.get('clinicalPathway') || '').trim(),
    [visitContext, query]
  );
  const complaintGroup = useMemo(
    () => String(visitContext?.complaintGroup || query.get('complaintGroup') || '').trim(),
    [visitContext, query]
  );

  const activeClinicalModules = useMemo(
    () => Array.isArray(visitContext?.activeClinicalModules) ? visitContext.activeClinicalModules : [],
    [visitContext]
  );
  const examinationPlan = useMemo(
    () => getSectionModulePlan({ modules: activeClinicalModules, section: 'examination' }),
    [activeClinicalModules]
  );
  const activeModuleLabels = useMemo(
    () => moduleLabels(activeClinicalModules),
    [activeClinicalModules]
  );
  const visibleSystemicExamFields = useMemo(() => {
    const priority = examinationPlan.examSystems || [];
    const priorityIndex = (key) => {
      const index = priority.indexOf(key);
      return index === -1 ? 999 : index;
    };
    return SYSTEMIC_EXAM_FIELDS
      .filter((item) => item.core || priority.includes(item.planKey))
      .sort((a, b) => {
        const diff = priorityIndex(a.planKey) - priorityIndex(b.planKey);
        if (diff !== 0) return diff;
        return SYSTEMIC_EXAM_FIELDS.indexOf(a) - SYSTEMIC_EXAM_FIELDS.indexOf(b);
      });
  }, [examinationPlan.examSystems]);

  const profileSummary = useMemo(() => {
    const parts = [];
    const ageDisplay = formatAgeAtVisit(workspaceVisit || {}, citizenAge);
    if (ageDisplay) parts.push(`Age ${ageDisplay}`);
    if (citizenAgeGroup !== 'unknown') parts.push(ageGroupLabel(citizenAgeGroup));
    if (activeCitizen?.gender) parts.push(String(activeCitizen.gender));
    return parts.join(' • ');
  }, [citizenAge, citizenAgeGroup, activeCitizen, workspaceVisit]);

  const contextChips = useMemo(() => {
    const out = [];
    if (visitCategory) out.push(`Service Area: ${visitCategory}`);
    if (clinicalPathway) out.push(`Clinical Focus: ${clinicalPathway}`);
    if (complaintGroup) out.push(`Primary Presenting Problem: ${complaintGroup}`);
    return out;
  }, [visitCategory, clinicalPathway, complaintGroup]);

  const showAnthropometry = useMemo(
    () => (
      ['under5', 'child', 'adolescent', 'geriatric'].includes(citizenAgeGroup) ||
      activeClinicalModules.some((key) => ['ncd', 'nutrition', 'paediatric', 'geriatric', 'metabolic'].includes(key))
    ),
    [citizenAgeGroup, activeClinicalModules]
  );

  const showRespiratoryEmphasis = useMemo(
    () => examinationPlan.examSystems.includes('respiratory'),
    [examinationPlan.examSystems]
  );

  const showObstetricExam = useMemo(
    () => citizenGender !== 'male' && activeClinicalModules.includes('maternal'),
    [citizenGender, activeClinicalModules]
  );

  const showFunctionalStatus = useMemo(
    () => citizenAgeGroup === 'geriatric' || activeClinicalModules.includes('geriatric'),
    [citizenAgeGroup, activeClinicalModules]
  );

  // Resolve ids
  useEffect(() => {
    const urlVisitId = visitIdProp || query.get('visitId') || '';
    const urlCitizenId = citizenIdProp || query.get('citizenId') || params.citizenId || '';
    const activeCitizenLocal = jget('activeCitizen') || {};
    const cid =
      urlCitizenId ||
      workspaceVisit?.citizenId ||
      activeCitizenLocal.citizenId ||
      localStorage.getItem('activeCitizenId') ||
      localStorage.getItem('citizenId') ||
      '';

    const scoped = cid ? localStorage.getItem(`activeVisitId:${cid}`) : '';
    const legacy = localStorage.getItem('activeVisitId') || localStorage.getItem('visitId') || '';
    const vid = urlVisitId || workspaceVisit?.visitId || scoped || legacy || '';

    setCitizenId(cid || '');
    setVisitId(vid || '');

    if (cid && vid) {
      localStorage.setItem('visitId', vid);
      localStorage.setItem('activeVisitId', vid);
      localStorage.setItem(`activeVisitId:${cid}`, vid);
    }
  }, [citizenIdProp, params.citizenId, query, visitIdProp, workspaceVisit]);

  // Read-only logic + intern override for returned visit edit
  const readOnlyByVisit = useVisitReadOnly(visitId, citizenId);
  const editFlag = (typeof window !== 'undefined' && localStorage.getItem('editReturnedVisit')) || '';
  const allowInternEditReturned = isIntern && visitId && editFlag === visitId;
  const readOnly = isCitizen || (readOnlyByVisit && !allowInternEditReturned);

  // Load
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!visitId) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setLoadError('');
        const r = await http.get(`/api/visits/${encodeURIComponent(visitId)}/examination`);
        if (!alive) return;

        const payload = r?.data || r || {};
        const exam = payload.examination || payload.exam || payload;
        const legacy = exam?.contextual?.legacy || {};

        setForm({
          vitals: exam?.vitals || {},
          generalExam: exam?.generalExam || exam?.general || legacy?.generalExam || {},
          systemicExam: exam?.systemicExam || exam?.systemic || legacy?.systemicExam || {},
        });
        setSaveSuccess('');
      } catch (e) {
        if (alive) setLoadError(e.message || 'Failed to load examination');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [visitId]);

  // Compute hasData
  const hasData = useMemo(() => {
    const v = form.vitals || {};
    const g = form.generalExam || {};
    const s = form.systemicExam || {};
    return (
      Object.values(v).some((x) => String(x ?? '').trim()) ||
      Object.values(g).some((x) => String(x ?? '').trim()) ||
      Object.values(s).some((x) => String(x ?? '').trim())
    );
  }, [form]);

  // Expose independent section save availability. The legacy event is retained
  // for the standalone encounter route while Workspace consumes direct callbacks.
  useEffect(() => {
    const can = !readOnly && hasData;
    if (typeof onCanSave === 'function') onCanSave(can);
    if (!embedded) {
      window.dispatchEvent(
        new CustomEvent('encounter:can-continue', {
          detail: { visitId, section: 'examination', can, canContinue: can },
        })
      );
    }
  }, [visitId, hasData, readOnly, onCanSave, embedded]);

  const setBusy = useCallback((busy) => {
    if (typeof onSavingChange === 'function') onSavingChange(Boolean(busy));
    if (!embedded) {
      window.dispatchEvent(
        new CustomEvent('encounter:busy', {
          detail: { visitId, section: 'examination', busy: Boolean(busy) },
        })
      );
    }
  }, [visitId, onSavingChange, embedded]);

  const save = useCallback(async () => {
    try {
      if (readOnly) return false;
      if (!visitId || !citizenId) {
        setSaveError('Missing visit or citizen');
        return false;
      }
      setBusy(true);
      setSaveError('');
      setSaveSuccess('');

      const v = form.vitals || {};
      const payload = {
        vitals: {
          pulse: toNum(v.pulse),
          bpSystolic: toNum(v.bpSystolic),
          bpDiastolic: toNum(v.bpDiastolic),
          tempC: toNum(v.tempC),
          respRate: toNum(v.respRate),
          spo2: toNum(v.spo2),
          heightCm: toNum(v.heightCm),
          weightKg: toNum(v.weightKg),
          bmi: toNum(v.bmi),
          waistCm: toNum(v.waistCm),
          hipCm: toNum(v.hipCm),
          whr: toNum(v.whr),
        },
        generalExam: { ...(form.generalExam || {}) },
        systemicExam: { ...(form.systemicExam || {}) },
      };

      const r = await http.put(`/api/visits/${encodeURIComponent(visitId)}/examination`, {
        citizenId,
        examination: payload,
      });
      if ((r && r.ok === false) || r?.data?.success === false) {
        throw new Error(r?.data?.message || 'Save failed');
      }

      setSaveSuccess('Examination saved');
      setLoadError('');
      if (typeof onSectionSaved === 'function') {
        await onSectionSaved(r?.data?.visit || r?.visit || null);
      }
      return true;
    } catch (e) {
      setSaveError(e.message || 'Save failed');
      return false;
    } finally {
      setBusy(false);
    }
  }, [visitId, citizenId, form, readOnly, onSectionSaved, setBusy]);

  // Workspace receives the section save function directly, avoiding collisions
  // with legacy global Save & Continue events from other mounted sections.
  useEffect(() => {
    if (typeof registerSave !== 'function') return undefined;
    registerSave(save);
    return () => registerSave(null);
  }, [registerSave, save]);

  // Listen for legacy save events only on the standalone Examination route.
  useEffect(() => {
    if (embedded) return undefined;

    const onSave = async (e) => {
      const d = e?.detail || {};
      if (d.visitId && d.visitId !== visitId) return;
      if (d.section && d.section !== 'examination') return;

      const ok = await save();
      if (!ok) return;

      if (d.intent === 'save-continue') {
        const cid =
          citizenId ||
          params.citizenId ||
          query.get('citizenId') ||
          jget('activeCitizen')?.citizenId ||
          localStorage.getItem('activeCitizenId') ||
          localStorage.getItem('citizenId') ||
          '';

        const url = cid
          ? `/citizen/${encodeURIComponent(cid)}/encounter/investigations?visitId=${encodeURIComponent(visitId)}`
          : `/citizen/encounter/investigations?visitId=${encodeURIComponent(visitId)}`;

        navigate(tenantUrl(url));
      }
    };
    window.addEventListener('encounter:save', onSave);
    return () => window.removeEventListener('encounter:save', onSave);
  }, [visitId, citizenId, params.citizenId, query, save, navigate, embedded]);

  // Update field handlers. Dirty state is owned by Workspace when embedded,
  // while the standalone page keeps the same input behaviour as before.
  const markDirty = useCallback(() => {
    setSaveSuccess('');
    if (typeof onSectionDirty === 'function') onSectionDirty();
  }, [onSectionDirty]);

  const handleVitalsChange = (e) => {
    const { name, value } = e.target;
    markDirty();
    setForm((f) => ({ ...f, vitals: { ...(f.vitals || {}), [name]: value ?? '' } }));
  };
  const handleGeneralChange = (e) => {
    const { name, value } = e.target;
    markDirty();
    setForm((f) => ({ ...f, generalExam: { ...(f.generalExam || {}), [name]: value ?? '' } }));
  };
  const handleSystemicChange = (e) => {
    const { name, value } = e.target;
    markDirty();
    setForm((f) => ({ ...f, systemicExam: { ...(f.systemicExam || {}), [name]: value ?? '' } }));
  };

  return (
    <div className={embedded ? 'text-black dark:text-white' : 'p-6 bg-white dark:bg-[#181a1b] min-h-screen text-black dark:text-white'}>
      {!embedded && <EncounterHeader visitId={visitId} />}

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {saveSuccess && <Alert severity="success" sx={{ mb: 2 }}>{saveSuccess}</Alert>}

          <Typography variant="h5" sx={{ mb: 2 }}>Clinical Examination</Typography>

          {!!activeCitizen?.citizenId && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {activeCitizen?.name || 'Citizen selected'}
              {profileSummary ? ` • ${profileSummary}` : ''}
            </Alert>
          )}

          {contextChips.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {contextChips.map((item) => (
                <Chip key={item} size="small" color="primary" variant="outlined" label={item} />
              ))}
            </Box>
          )}

          {activeModuleLabels.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {activeModuleLabels.map((item) => (
                <Chip key={item.key} size="small" variant="outlined" label={`Focused module: ${item.label}`} />
              ))}
            </Box>
          )}

          <Alert severity="info" sx={{ mb: 2 }}>
            This examination page is simplified from the selected citizen’s age group, gender, and visit context.
            Relevant examination blocks are shown first while the same save structure is preserved.
          </Alert>

          <Section title="Vitals">
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 2 }}>
              <TextField
                name="pulse"
                label="Pulse (bpm)"
                value={form.vitals?.pulse || ''}
                onChange={handleVitalsChange}
                fullWidth
                sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                disabled={readOnly}
                {...fieldProps}
              />
              <TextField
                name="bpSystolic"
                label="BP Systolic"
                value={form.vitals?.bpSystolic || ''}
                onChange={handleVitalsChange}
                fullWidth
                sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                disabled={readOnly}
                {...fieldProps}
              />
              <TextField
                name="bpDiastolic"
                label="BP Diastolic"
                value={form.vitals?.bpDiastolic || ''}
                onChange={handleVitalsChange}
                fullWidth
                sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                disabled={readOnly}
                {...fieldProps}
              />
              <TextField
                name="tempC"
                label="Temperature (°C)"
                value={form.vitals?.tempC || ''}
                onChange={handleVitalsChange}
                fullWidth
                sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                disabled={readOnly}
                {...fieldProps}
              />
              <TextField
                name="respRate"
                label="Respiratory Rate"
                value={form.vitals?.respRate || ''}
                onChange={handleVitalsChange}
                fullWidth
                sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                disabled={readOnly}
                {...fieldProps}
              />
              <TextField
                name="spo2"
                label="SpO₂ (%)"
                value={form.vitals?.spo2 || ''}
                onChange={handleVitalsChange}
                fullWidth
                sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                disabled={readOnly}
                {...fieldProps}
              />
              <TextField
                name="weightKg"
                label="Weight (kg)"
                value={form.vitals?.weightKg || ''}
                onChange={handleVitalsChange}
                fullWidth
                sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                disabled={readOnly}
                helperText="Available to Prescription for reviewed weight-based dose support."
                {...fieldProps}
              />
            </Box>
          </Section>

          {showAnthropometry && (
            <Section title="Anthropometry">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12,1fr)', gap: 2 }}>
                <TextField
                  name="heightCm"
                  label="Height (cm)"
                  value={form.vitals?.heightCm || ''}
                  onChange={handleVitalsChange}
                  fullWidth
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 6' } }}
                  disabled={readOnly}
                  {...fieldProps}
                />
                <TextField
                  name="bmi"
                  label="BMI"
                  value={form.vitals?.bmi || ''}
                  onChange={handleVitalsChange}
                  fullWidth
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  disabled={readOnly}
                  {...fieldProps}
                />
                <TextField
                  name="waistCm"
                  label="Waist (cm)"
                  value={form.vitals?.waistCm || ''}
                  onChange={handleVitalsChange}
                  fullWidth
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  disabled={readOnly}
                  {...fieldProps}
                />
                <TextField
                  name="hipCm"
                  label="Hip (cm)"
                  value={form.vitals?.hipCm || ''}
                  onChange={handleVitalsChange}
                  fullWidth
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  disabled={readOnly}
                  {...fieldProps}
                />
                <TextField
                  name="whr"
                  label="WHR"
                  value={form.vitals?.whr || ''}
                  onChange={handleVitalsChange}
                  fullWidth
                  sx={{ gridColumn: { xs: 'span 12', sm: 'span 4' } }}
                  disabled={readOnly}
                  {...fieldProps}
                />
              </Box>
            </Section>
          )}

          <Section title="General Examination">
            <TextField
              name="generalAppearance"
              label="General appearance"
              value={form.generalExam?.generalAppearance || ''}
              onChange={handleGeneralChange}
              fullWidth
              multiline
              minRows={2}
              disabled={readOnly}
              {...fieldProps}
            />
            <Box sx={{ height: 12 }} />
            <TextField
              name="lymphNodes"
              label="Lymph nodes"
              value={form.generalExam?.lymphNodes || ''}
              onChange={handleGeneralChange}
              fullWidth
              multiline
              minRows={2}
              disabled={readOnly}
              {...fieldProps}
            />
            <Box sx={{ height: 12 }} />
            <TextField
              name="edema"
              label="Edema"
              value={form.generalExam?.edema || ''}
              onChange={handleGeneralChange}
              fullWidth
              multiline
              minRows={2}
              disabled={readOnly}
              {...fieldProps}
            />
          </Section>

          {showObstetricExam && (
            <Section title="Obstetric / Maternal Examination">
              <TextField
                name="obstetric"
                label="Obstetric / maternal findings"
                value={form.generalExam?.obstetric || ''}
                onChange={handleGeneralChange}
                fullWidth
                multiline
                minRows={3}
                disabled={readOnly}
                {...fieldProps}
              />
            </Section>
          )}

          <Section title={showRespiratoryEmphasis ? 'Systemic Examination (focused systems first)' : 'Systemic Examination'}>
            {visibleSystemicExamFields.map((item) => {
              const isPriority = examinationPlan.examSystems.includes(item.planKey);
              return (
                <Box key={item.name} sx={{ mb: 1.5 }}>
                  <TextField
                    name={item.name}
                    label={item.label}
                    value={form.systemicExam?.[item.name] || ''}
                    onChange={handleSystemicChange}
                    fullWidth
                    multiline
                    minRows={2}
                    disabled={readOnly}
                    helperText={isPriority ? 'Prioritised by the active Visit pathway/modules; record only clinically relevant findings.' : ''}
                    {...fieldProps}
                  />
                </Box>
              );
            })}
          </Section>

          {showFunctionalStatus && (
            <Section title="Functional / Geriatric Screening">
              <TextField
                name="functionalStatus"
                label="Functional status / mobility"
                value={form.generalExam?.functionalStatus || ''}
                onChange={handleGeneralChange}
                fullWidth
                multiline
                minRows={2}
                disabled={readOnly}
                {...fieldProps}
              />
              <Box sx={{ height: 12 }} />
              <TextField
                name="falls"
                label="Falls / gait / balance"
                value={form.generalExam?.falls || ''}
                onChange={handleGeneralChange}
                fullWidth
                multiline
                minRows={2}
                disabled={readOnly}
                {...fieldProps}
              />
            </Section>
          )}
        </>
      )}
    </div>
  );
}
